import { config } from '@iheartjane/config';
import {
  Button,
  DesktopIcon,
  Flex,
  MobileIcon,
  contrastingColor,
  useToast,
} from '@iheartjane/reefer';
import type { MenuColorsConfig, MenuComponentConfig } from '@iheartjane/types';
import { clsx } from 'clsx';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

import {
  useFetchCurrentBloomMenu,
  useUpdateBloomMenu,
} from '@jane/business-admin/data-access';
import { businessPaths, toBloomFontFamily } from '@jane/business-admin/util';
import { useGoogleFonts } from '@jane/shared-ecomm/data-access';

import { EditorPreview } from './components/menuEditor/EditorPreview';
import { EditorSidebar } from './components/menuEditor/EditorSidebar';
import type { SidebarSection } from './components/menuEditor/EditorSidebar';
import { ModalPageWrapper } from './components/ModalPageWrapper';

import styles from './PremiumMenuEditor.module.css';

const CONTRAST_OPTIONS = ['#0E0E0E', '#FFFFFF'];

const withContrast = (main: string) => ({
  main,
  contrast: contrastingColor(main, CONTRAST_OPTIONS),
});

export const buildThemeColors = (colors: MenuColorsConfig) => {
  const primary = withContrast(colors.primary?.main || '');

  return {
    primary,
    secondary: withContrast(colors.secondary?.main || ''),
    deals: withContrast(colors.deals?.main || ''),
    header: withContrast(colors.header?.main || ''),
    text: {
      primary_inverse: primary.contrast === '#0E0E0E' ? '#FFFFFF' : '#000000',
    },
    lineage: {
      hybrid: withContrast(colors.lineage?.hybrid?.main || ''),
      indica: withContrast(colors.lineage?.indica?.main || ''),
      sativa: withContrast(colors.lineage?.sativa?.main || ''),
      cbd: withContrast(colors.lineage?.cbd?.main || ''),
    },
  };
};

export const PremiumMenuEditor = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { data: bloomMenuData } = useFetchCurrentBloomMenu();
  const { mutate: updateBloomMenu, isLoading } = useUpdateBloomMenu(
    bloomMenuData?.id
  );
  const [activeSection, setActiveSection] = useState<SidebarSection | null>(
    null
  );
  const [viewMode, setViewMode] = useState<'desktop' | 'mobile'>('desktop');
  const { data: fontData, isLoading: fontsLoading } = useGoogleFonts({
    apiKey: config.googleFontsKey,
  });

  const supportedFontFamilies = useMemo(() => fontData ?? [], [fontData]);
  const googleFontFromBloomFont = useCallback(
    (font_family?: string) => {
      return supportedFontFamilies.find(({ family }) => family === font_family);
    },
    [supportedFontFamilies]
  );

  const logoUrl = bloomMenuData?.logo?.large ?? undefined;
  const primaryColor =
    bloomMenuData?.theme?.bloom?.colors?.primary?.main ?? undefined;
  const headerColor =
    bloomMenuData?.theme?.bloom?.colors?.header?.main ?? undefined;

  const formValuesFromBloomMenu = useMemo<MenuComponentConfig>(
    () => ({
      category_tabs: bloomMenuData?.theme?.bloom?.category_tabs || {
        position: 'above_carousel',
        style: 'icon',
      },
      colors: {
        primary: {
          main: bloomMenuData?.theme?.bloom?.colors?.primary?.main,
        },
        secondary: {
          main: bloomMenuData?.theme?.bloom?.colors?.secondary?.main,
        },
        deals: {
          main: bloomMenuData?.theme?.bloom?.colors?.deals?.main,
        },
        header: {
          main: bloomMenuData?.theme?.bloom?.colors?.header?.main,
        },
        lineage: {
          hybrid: {
            main: bloomMenuData?.theme?.bloom?.colors?.lineage?.hybrid?.main,
          },
          indica: {
            main: bloomMenuData?.theme?.bloom?.colors?.lineage?.indica?.main,
          },
          sativa: {
            main: bloomMenuData?.theme?.bloom?.colors?.lineage?.sativa?.main,
          },
          cbd: {
            main: bloomMenuData?.theme?.bloom?.colors?.lineage?.cbd?.main,
          },
        },
      },
      fonts: {
        heading: googleFontFromBloomFont(
          bloomMenuData?.theme?.bloom?.fonts?.heading?.font_family
        ),
        body: googleFontFromBloomFont(
          bloomMenuData?.theme?.bloom?.fonts?.body?.font_family
        ),
      },
      logo: logoUrl,
      product_cards: bloomMenuData?.theme?.bloom?.product_cards || {
        card_size: 'medium',
        card_border: false,
        hover: 'shadow',
        cta_icon: 'add',
        cta_style: 'card_bottom_full',
      },
      search: bloomMenuData?.theme?.bloom?.search || {
        position: 'split',
      },
    }),
    [bloomMenuData, googleFontFromBloomFont]
  );

  const formMethods = useForm<MenuComponentConfig>({
    mode: 'all',
    reValidateMode: 'onChange',
    values: formValuesFromBloomMenu,
    defaultValues: formValuesFromBloomMenu,
  });
  const {
    getValues,
    formState: { isValid },
  } = formMethods;
  const formValues = getValues();

  const onPreview = useCallback(() => {
    updateBloomMenu(
      {
        theme_draft: {
          ...bloomMenuData?.theme?.bloom,
          category_tabs: formValues.category_tabs,
          colors: buildThemeColors(formValues.colors),
          fonts: {
            heading: formValues.fonts?.heading
              ? toBloomFontFamily(formValues.fonts.heading)
              : null,
            body: formValues.fonts?.body
              ? toBloomFontFamily(formValues.fonts.body)
              : null,
          },
          product_cards: formValues.product_cards,
          search: formValues.search,
        },
        logo: formValues.logo || undefined,
      },
      {
        onError: () => {
          toast.add({
            label: 'An error occurred.',
            variant: 'error',
          });
        },
        onSuccess: () => {
          if (!bloomMenuData?.menu_url) {
            return;
          }
          const previewUrl = new URL(bloomMenuData.menu_url);
          previewUrl.searchParams.set('draft', 'true');
          const newWindow = window.open(
            previewUrl,
            '_blank',
            'noopener,noreferrer'
          );
          if (newWindow) {
            newWindow.opener = null;
          }
        },
      }
    );
  }, [formValues]);

  const onPublish = useCallback(
    (data: MenuComponentConfig) => {
      updateBloomMenu(
        {
          theme: {
            ...bloomMenuData?.theme?.bloom,
            category_tabs: data.category_tabs,
            colors: buildThemeColors(data.colors),
            fonts: {
              heading: data.fonts?.heading
                ? toBloomFontFamily(data.fonts.heading)
                : null,
              body: data.fonts?.body
                ? toBloomFontFamily(data.fonts.body)
                : null,
            },
            product_cards: data.product_cards,
            search: data.search,
          },
          logo: data.logo || undefined,
        },
        {
          onError: () => {
            toast.add({
              label: 'An error occurred.',
              variant: 'error',
            });
          },
          onSuccess: () => {
            toast.add({
              label: 'Menu updated!',
              variant: 'success',
            });
            navigate(businessPaths.premium());
          },
        }
      );
    },
    [bloomMenuData?.theme?.bloom, navigate, toast, updateBloomMenu]
  );

  return (
    <ModalPageWrapper
      formMethods={formMethods}
      isLoading={isLoading || fontsLoading}
      onSubmit={onPublish}
      modalOverflow="hidden"
      centerContent={
        <Flex
          background="grays-white"
          borderRadius="sm"
          border="grays-light"
          p={4}
        >
          <Button
            className={clsx({
              [styles['viewMode_unselected']]: viewMode !== 'desktop',
            })}
            startIcon={<DesktopIcon />}
            label="Desktop"
            onClick={() => setViewMode('desktop')}
            variant={viewMode === 'desktop' ? 'primary' : 'tertiary'}
          />
          <Button
            className={clsx({
              [styles['viewMode_unselected']]: viewMode !== 'mobile',
            })}
            startIcon={<MobileIcon />}
            label="Mobile"
            onClick={() => setViewMode('mobile')}
            variant={viewMode === 'mobile' ? 'primary' : 'tertiary'}
          />
        </Flex>
      }
      preview={{
        onClick: onPreview,
        disable: !isValid || isLoading,
        label: 'Preview',
      }}
      title="Menu editor"
    >
      <Flex p={40} gap={40} height="calc(100vh - 80px)">
        <EditorSidebar
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          supportedFontFamilies={supportedFontFamilies}
        />

        <EditorPreview
          activeSection={activeSection}
          logoUrl={logoUrl}
          primaryColor={primaryColor}
          headerColor={headerColor}
          viewMode={viewMode}
        />
      </Flex>
    </ModalPageWrapper>
  );
};
